import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import {
  DmCombined, DmDocument, DmHistory, DmItem, DmLibraryFile, DmOtherFile, DmPage, DmParty, DocumentManagerService
} from '../../services/report/document-manager.service';

// A ticked document: what it is, and the label it shows in the combine list.
interface Ticked { item: DmItem; label: string; combinable: boolean; }

/**
 * Document Manager (hosted mockup document-manager; moved from twin-ci <role>/inspections_reports).
 * Left: Combine Multiple PDFs, Upload Other Files, Select Parties and PDFs for Email. Right: the
 * job's Reports/Documents, Combined PDFs, Other Files and the Document Library, each with a tick
 * box. Ticking adds a PDF to the combine list in the order ticked (twin-ci), and every ticked
 * document is attached to the email in page order (agreed 2026-11-04). What each role may do comes
 * from the API (`can`); the API refuses the rest anyway.
 */
@Component({
  selector: 'app-inspection-reports',
  templateUrl: './inspection-reports.component.html',
  styleUrls: ['./inspection-reports.component.scss']
})
export class InspectionReportsComponent implements OnInit, OnDestroy {
  page_title = 'Inspection: Reports / Document Manager';
  jobId: string;
  data: DmPage;
  loading = true;
  loadError = '';

  // Accordion (mockup: Email open by default).
  open = { combine: false, upload: false, email: true };

  // Tick boxes, keyed "kind" or "kind:id", in the order ticked.
  ticked: { [key: string]: Ticked } = {};
  tickOrder: string[] = [];

  // Upload Other Files.
  uploadName = '';
  uploadFile: File = null;
  @ViewChild('fileInput') fileInput: ElementRef;

  // Email.
  recipients: { [key: string]: boolean } = {};
  extra: string[] = [];
  replyId: number = null;
  sendOriginal = false;
  @ViewChild('editor') editor: ElementRef;
  textStyle = 'p';

  resendTo: { [id: number]: string } = {};
  busy = '';            // the action in progress, e.g. "combine", "email", "docusign:combined:3"
  notice: { type: 'success' | 'error', text: string } = null;
  emailResults: { to: string, ok: boolean, message: string }[] = [];

  private sub: Subscription;

  constructor(private route: ActivatedRoute, private dm: DocumentManagerService) { }

  ngOnInit() {
    this.sub = this.route.params.subscribe(params => {
      this.jobId = params.jobId;
      this.ticked = {};
      this.tickOrder = [];
      this.recipients = {};
      this.load(true);
    });
  }

  ngOnDestroy() {
    if (this.sub) { this.sub.unsubscribe(); }
  }

  load(first = false) {
    this.loading = first;
    this.loadError = '';
    this.dm.page(this.jobId).subscribe(page => {
      this.data = page;
      this.loading = false;
      this.forgetMissing();
      if (first) {
        // twin-ci: Document Library files marked auto-attach start ticked.
        page.library.filter(l => l.auto_attach).forEach(l => this.tick({ kind: 'library', id: l.id }, l.name, false, true));
        page.history.forEach(h => this.resendTo[h.id] = h.to_email);
      }
    }, error => {
      this.loading = false;
      this.loadError = typeof error === 'string' && error ? error : 'The Document Manager could not be loaded.';
    });
  }

  // ---- ticking -----------------------------------------------------------------------------

  key(item: DmItem) { return item.id ? item.kind + ':' + item.id : item.kind; }

  isTicked(item: DmItem) { return !!this.ticked[this.key(item)]; }

  tick(item: DmItem, label: string, combinable: boolean, on: boolean) {
    const key = this.key(item);
    if (on && !this.ticked[key]) {
      this.ticked[key] = { item, label, combinable };
      this.tickOrder.push(key);
    } else if (!on && this.ticked[key]) {
      delete this.ticked[key];
      this.tickOrder = this.tickOrder.filter(k => k !== key);
    }
  }

  // After a reload: drop ticks for documents that are gone (deleted combined PDFs / files).
  private forgetMissing() {
    const exists = new Set<string>();
    this.data.documents.filter(d => d.file).forEach(d => exists.add(d.key));
    this.data.combined.forEach(c => exists.add('combined:' + c.id));
    this.data.other_files.forEach(o => exists.add('other:' + o.id));
    this.data.library.forEach(l => exists.add('library:' + l.id));
    this.tickOrder.filter(k => !exists.has(k)).forEach(k => this.tick(this.ticked[k].item, '', false, false));
  }

  get combineList(): Ticked[] {
    return this.tickOrder.map(k => this.ticked[k]).filter(t => t.combinable);
  }

  get combineText(): string {
    return this.combineList.map((t, i) => (i + 1) + '. ' + t.label).join('\n');
  }

  clearList() {
    this.combineList.forEach(t => this.tick(t.item, '', false, false));
  }

  docItem(d: DmDocument): DmItem { return { kind: d.key }; }
  comboItem(c: DmCombined): DmItem { return { kind: 'combined', id: c.id }; }
  otherItem(o: DmOtherFile): DmItem { return { kind: 'other', id: o.id }; }
  libItem(l: DmLibraryFile): DmItem { return { kind: 'library', id: l.id }; }

  // ---- actions -----------------------------------------------------------------------------

  private done(text: string) {
    this.busy = '';
    this.notice = { type: 'success', text };
  }

  private failed(error, fallback: string) {
    this.busy = '';
    this.notice = { type: 'error', text: typeof error === 'string' && error && error !== 'The given data was invalid.' ? error : fallback };
  }

  combine() {
    const list = this.combineList;
    if (list.length < 2) {
      this.notice = { type: 'error', text: 'Tick at least two PDFs, in the order you want them, to combine them.' };
      return;
    }
    this.busy = 'combine';
    this.notice = null;
    this.dm.combine(this.jobId, list.map(t => t.item)).subscribe(res => {
      this.clearList();
      this.done(res.message || 'Your combined PDF has been created.');
      this.load();
    }, error => this.failed(error, 'The PDFs could not be combined.'));
  }

  deleteCombined(c: DmCombined) {
    this.confirmDelete('Delete this combined PDF?', c.name).then(ok => {
      if (!ok) { return; }
      this.busy = 'delete';
      this.dm.deleteCombined(this.jobId, c.id).subscribe(res => { this.done(res.message); this.load(); },
        error => this.failed(error, 'The combined PDF could not be deleted.'));
    });
  }

  fileChosen(event) {
    const files = event.target.files;
    this.uploadFile = files && files.length ? files[0] : null;
  }

  upload() {
    if (!this.uploadName.trim() || !this.uploadFile) {
      this.notice = { type: 'error', text: 'Enter a name and choose a PDF or JPG file to upload.' };
      return;
    }
    if (!/\.(pdf|jpe?g|png)$/i.test(this.uploadFile.name)) {
      this.notice = { type: 'error', text: 'Upload a PDF, JPG or PNG file.' };
      return;
    }
    if (this.data && this.uploadFile.size > this.data.upload_max_kb * 1024) {
      this.notice = { type: 'error', text: 'The file is larger than ' + Math.round(this.data.upload_max_kb / 1024) + ' MB.' };
      return;
    }
    this.busy = 'upload';
    this.notice = null;
    this.dm.upload(this.jobId, this.uploadName.trim(), this.uploadFile).subscribe(res => {
      this.uploadName = '';
      this.uploadFile = null;
      if (this.fileInput) { this.fileInput.nativeElement.value = ''; }
      this.done(res.message || 'File uploaded.');
      this.load();
    }, error => this.failed(error, 'The file could not be uploaded.'));
  }

  deleteOther(o: DmOtherFile) {
    this.confirmDelete('Delete this file?', o.name).then(ok => {
      if (!ok) { return; }
      this.busy = 'delete';
      this.dm.deleteOther(this.jobId, o.id).subscribe(res => { this.done(res.message); this.load(); },
        error => this.failed(error, 'The file could not be deleted.'));
    });
  }

  download(item: DmItem, name: string) {
    this.busy = 'download:' + this.key(item);
    this.dm.download(this.jobId, item).subscribe(blob => {
      this.busy = '';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, error => this.failed(error, '"' + name + '" could not be downloaded.'));
  }

  docusign(kind: 'contract' | 'combined' | 'other', id?: number) {
    this.busy = 'docusign:' + kind + ':' + (id || '');
    this.notice = null;
    this.dm.docusign(this.jobId, kind, id).subscribe(res => {
      this.done((res.message || 'Draft envelope created in DocuSign.') + ' Use "Open" to finish it in DocuSign.');
      this.load();
    }, error => this.failed(error, 'The document could not be sent to DocuSign.'));
  }

  setReportSent(sent: boolean) {
    this.busy = 'report-sent';
    this.dm.setReportSent(this.jobId, sent).subscribe(res => {
      this.busy = '';
      this.data.report_sent = res.report_sent;
    }, error => this.failed(error, 'Report Sent could not be changed.'));
  }

  // ---- email -------------------------------------------------------------------------------

  partyKey(p: DmParty) { return p.kind + ':' + p.id; }

  addRecipient() {
    if (this.extra.length < 4) { this.extra.push(''); }
  }

  removeRecipient(i: number) {
    this.extra.splice(i, 1);
  }

  trackIndex(i: number) { return i; }

  replyChosen() {
    if (!this.replyId) { return; }
    this.dm.reply(this.jobId, this.replyId).subscribe(reply => {
      if (this.editor) { this.editor.nativeElement.innerHTML = reply.reply || ''; }
    }, error => this.failed(error, 'The canned reply could not be loaded.'));
  }

  format(command: string, value: string = null) {
    document.execCommand(command, false, value);
    if (this.editor) { this.editor.nativeElement.focus(); }
  }

  get editorHtml(): string {
    const el = this.editor ? this.editor.nativeElement : null;
    return el && el.textContent.trim() !== '' ? el.innerHTML : '';
  }

  /** Every ticked document, in page order (the API orders them the same way). */
  get attachments(): DmItem[] {
    if (!this.data) { return []; }
    const items: DmItem[] = [];
    this.data.documents.filter(d => d.file).forEach(d => items.push(this.docItem(d)));
    this.data.combined.forEach(c => items.push(this.comboItem(c)));
    this.data.other_files.forEach(o => items.push(this.otherItem(o)));
    this.data.library.forEach(l => items.push(this.libItem(l)));
    return items.filter(i => this.isTicked(i));
  }

  email() {
    const recipients = this.data.parties.filter(p => this.recipients[this.partyKey(p)]).map(p => ({ kind: p.kind, id: p.id }));
    const extra = this.extra.map(e => e.trim()).filter(e => e !== '');
    const attachments = this.attachments;
    const invalid = extra.filter(e => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (!recipients.length && !extra.length) {
      this.notice = { type: 'error', text: 'Choose at least one interested party or add an email address.' };
      return;
    }
    if (invalid.length) {
      this.notice = { type: 'error', text: 'Check the additional recipient address: ' + invalid.join(', ') + '.' };
      return;
    }
    if (!attachments.length) {
      this.notice = { type: 'error', text: 'Tick at least one document on the right to attach.' };
      return;
    }
    const count = recipients.length + extra.length;
    Swal({
      title: 'Send ' + attachments.length + ' document' + (attachments.length === 1 ? '' : 's') + '?',
      text: 'One email to each of the ' + count + ' recipient' + (count === 1 ? '' : 's') + ', with every ticked document attached.',
      type: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send'
    }).then(result => {
      if (!result.value) { return; }
      this.busy = 'email';
      this.notice = null;
      this.emailResults = [];
      this.dm.email(this.jobId, {
        recipients, extra, attachments, reply_id: this.replyId || null, text: this.editorHtml, send_original: this.sendOriginal
      }).subscribe(res => {
        this.busy = '';
        this.emailResults = res.results || [];
        const failed = this.emailResults.filter(r => !r.ok).length;
        this.notice = failed
          ? { type: 'error', text: failed + ' of ' + this.emailResults.length + ' emails could not be sent.' }
          : { type: 'success', text: 'Email sent to ' + this.emailResults.length + ' recipient' + (this.emailResults.length === 1 ? '' : 's') + '.' };
        this.load();
      }, error => this.failed(error, 'The email could not be sent.'));
    });
  }

  resend(h: DmHistory) {
    const email = (this.resendTo[h.id] || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this.notice = { type: 'error', text: 'Enter a valid email address to resend to.' };
      return;
    }
    this.busy = 'resend:' + h.id;
    this.dm.resend(this.jobId, h.id, email).subscribe(res => { this.done(res.message); this.load(); },
      error => this.failed(error, 'The confirmation email could not be resent.'));
  }

  // ---- display -----------------------------------------------------------------------------

  historyType(type: string) {
    return ({ attachment: 'Documents', confimation: 'Confirmation', 're-confimation': 'Confirmation (resent)' } as any)[type] || type;
  }

  private confirmDelete(title: string, name: string): Promise<boolean> {
    return Swal({
      title, text: name, type: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#a52c2c'
    }).then(result => !!result.value);
  }
}
